import { GridSingleSelectColDef, GridRenderCellParams, getGridDefaultColumnTypes, DEFAULT_GRID_COL_TYPE_KEY } from "@mui/x-data-grid";
import { includes, toLower, toNumber } from "lodash";
import { ContentColumn } from "../../../hooks/azureTableHooks";
import { customSingleSelectOperator } from "../CustomFilterOperators";

const defaultColumnTypes = {
  ...getGridDefaultColumnTypes(),
  float: getGridDefaultColumnTypes().number,
}

const availableFilterOperators = [
  "equals", "startsWith", "endsWith", "contains", ">", "<", ">=", "<=", "=", "!=", "before", "after", "is", "isAnyOf"
]

export default function getDataColumnContent(c: ContentColumn): GridSingleSelectColDef {
  const filterOperators = defaultColumnTypes[c.dataType ?? DEFAULT_GRID_COL_TYPE_KEY]?.filterOperators  || defaultColumnTypes.string?.filterOperators;

  const filters = filterOperators?.filter(o => includes(availableFilterOperators, o?.value)) ?? [];

  const isSingleSelect = c.dataType === "singleSelect";

  const columnFilters = isSingleSelect ? [customSingleSelectOperator] : filters;

  const decimalPlaces = c.decimalPlaces || undefined;

  // Shared numeric parsing function
  const parseNumericValue = (value: any): number | null => {
    if (typeof value === 'number') {
      return value;
    }
    
    if (typeof value === 'string') {
      // First, normalize the input by replacing any spaces
      let cleanValue = value.trim().replace(/\s/g, '');
      
      // Check if the value starts with a minus sign (negative number)
      // Handle different types of minus signs: ASCII hyphen-minus, en dash, em dash, minus sign
      const negativeChars = ['-', '−', '–', '—'];
      const isNegative = negativeChars.some(char => cleanValue.startsWith(char));
      if (isNegative) {
        // Remove the first character (whichever type of minus sign it is)
        cleanValue = cleanValue.slice(1);
      }
      
      // Handle the case where the last character is a comma or dot
      if (cleanValue.endsWith(',') || cleanValue.endsWith('.')) {
        cleanValue = cleanValue.slice(0, -1);
      }
      
      // If there are multiple decimal separators, keep only the last one
      const lastCommaIndex = cleanValue.lastIndexOf(',');
      const lastDotIndex = cleanValue.lastIndexOf('.');
      
      if (lastCommaIndex !== -1 && lastDotIndex !== -1) {
        // If both separators exist, keep the last one
        const lastSeparatorIndex = Math.max(lastCommaIndex, lastDotIndex);
        cleanValue = cleanValue.slice(0, lastSeparatorIndex) + '.' + cleanValue.slice(lastSeparatorIndex + 1);
      } else if (lastCommaIndex !== -1) {
        // If only comma exists, convert it to dot
        cleanValue = cleanValue.replace(',', '.');
      }
      
      // Remove any remaining non-numeric characters except for the decimal point
      cleanValue = cleanValue.replace(/[^\d.]/g, '');
      
      // Re-add the minus sign if the original value was negative
      if (isNegative) {
        cleanValue = '-' + cleanValue;
      }
      
      // Parse the cleaned value
      const parsedValue = parseFloat(cleanValue);
      
      // Return the parsed value if it's a valid number, otherwise return null
      return isNaN(parsedValue) ? null : parsedValue;
    }
    
    return null;
  };

  return {
    field: c.accessor,
    headerName: c.name,
    type: c.dataType as any,
    editable: !c.readonly,
    flex: c.width ? undefined : 1,
    valueOptions: c.valueOptions?.map(o => {
      return {
        value: o.value,
        label: o.label
      }
    }),

    valueParser: (value: any, row) => {
      if (toLower(c.accessor) === "ky"){
        return value
      }
      if (c.dataType === "float" || c.dataType === "number") {
        return parseNumericValue(value);
      } else if (c.dataType === "singleSelect") {
        // If the value is not a number, try to find it in valueOptions
        if (typeof value === 'string' && isNaN(Number(value))) {
          const option = c.valueOptions?.find(opt => opt.label.toLowerCase() === value.toLowerCase());
          if (option) {
            return option.value;
          }
        }
        // If it's a number or not found in labels, return the original value
        return value;
      }
      // For other data types, return the original value
      return value;
    },

    pastedValueParser: (value, row) => {
      // Handle empty strings by converting them to null
      // This ensures empty cells overwrite existing values when pasted
      if (value === '') {
        return null;
      }
      
      if (c.dataType === "float" || c.dataType === "number") {
        return parseNumericValue(value);
      } else if (c.dataType === "singleSelect") {
        // If the value is not a number, try to find it in valueOptions
        if (typeof value === 'string' && isNaN(Number(value))) {
          const option = c.valueOptions?.find(opt => opt.label.toLowerCase() === value.toLowerCase());
          if (option) {
            return option.value;
          }
        }
        // If it's a number or not found in labels, return the original value
        return value;
      }
      return value;
    },

    valueGetter: (value, row) => {
      if (c.dataType === "dateTime" || c.dataType === "date") {
        if (!value) return undefined;
        return new Date(value as string);
      }
      // For numeric values, return the raw value to ensure proper editing
      return value;
    },

    valueFormatter: (value, row) => {
      if (toLower(c.accessor) === "ky"){
        const numValue = Number(value);
        return numValue.toLocaleString('et-EE', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          useGrouping: false,
        });
      }

      if (c.dataType === "float" || c.dataType === "number") {
        if (value === null || value === undefined || c.accessor === "Ky") return value;
        const numValue = Number(value);
        if (isNaN(numValue)) return value; // Return original value if it's not a valid number
        return numValue.toLocaleString('et-EE', {
          minimumFractionDigits: 0,
          maximumFractionDigits: decimalPlaces,
          useGrouping: c.thousandSeparator || false,
        });
      }
      
      if (c.dataType === "singleSelect") {
        // For single select, find the corresponding label for the value
        const option = c.valueOptions?.find(opt => opt.value === value);
        return option ? option.label : value;
      }
      
      return value;
    },

    valueSetter: (value, row) => {
      if (c.dataType === "float" || c.dataType === "number") {
        const parsedValue = parseNumericValue(value);
        return { ...row, [c.accessor]: parsedValue };
      }
      return { ...row, [c.accessor]: value };
    },
    width: c.width,
    cellClassName: c.readonly ? "readonly-cell" : "",
    filterOperators: columnFilters,
    filterable: columnFilters.length > 0,
    renderCell: (params: GridRenderCellParams) => { 
      if(c.accessor === "Ky" && (toNumber(params?.value) < 0 || isNaN(toNumber(params?.value)))) return <span>-1</span>
    },
  }
}